import { useCallback, useMemo } from 'react';
import { useGetCustomersQuery } from '@/lib/api/lookupsApi';
import { useModuleLookups } from '@/hooks';
import { buildLookupOptions } from '@/config/formOptions';
import { buildRawMaterialTypeOptions } from '@/config/rawMaterialTypes.constants';
import { resolveEquivalentUnit, hasMaterialDensity } from '@/lib/mappers';
import { EMPTY_LIST } from '@/lib/utils';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { useGetOrderForShipmentQuery, useCreateShipmentForOrderMutation } from './api/ordersApi';

// Everything <OrderForm> needs from the server — see slices/orders/CLAUDE.md.
export const useOrderFormOptions = () => {
  // Default stockCheck: a site stocking none of the account's materials sinks to the bottom.
  const { certificateType, sites, materials, facilityOptions, materialsLoading } =
    useModuleLookups();
  const { data: customers = EMPTY_LIST, isLoading: customersLoading } = useGetCustomersQuery();

  const options = useMemo(
    () => ({
      facilities: facilityOptions,
      customers: buildLookupOptions(customers),
      rawMaterialTypes: buildRawMaterialTypeOptions(),
      certificateType,
    }),
    [facilityOptions, customers, certificateType],
  );

  // Resolved at submit time, where the material is only known by id.
  const getEquivalentUnit = useCallback(
    (values) => {
      const material = materials.find((item) => item.id === values.materialId) ?? null;
      return resolveEquivalentUnit({ unit: values.unit, material });
    },
    [materials],
  );

  return {
    options,
    sites,
    materials,
    getEquivalentUnit,
    isLoading: materialsLoading || customersLoading,
  };
};

// Everything the Update shipment form needs from the server, in one call.
export const useOrderShipmentFormOptions = () => {
  const { certificateType, sites, materials, facilityOptions, materialsLoading } =
    useModuleLookups();
  const { data: customers = EMPTY_LIST, isLoading: customersLoading } = useGetCustomersQuery();

  const options = useMemo(
    () => ({
      facilities: facilityOptions,
      customers: buildLookupOptions(customers),
      sites,
      materials,
      certificateType,
    }),
    [facilityOptions, customers, sites, materials, certificateType],
  );

  return {
    options,
    isLoading: materialsLoading || customersLoading,
  };
};

// The material unit matching the opener's unit type; Working only when the material can convert it.
const inheritedShipmentUnit = (material, unitType) => {
  if (!material || !unitType) return '';
  if (unitType === UNIT_TYPES.working) {
    return hasMaterialDensity(material) ? material.workingUnit : '';
  }
  return material.declarationUnit ?? '';
};

// Update shipment's data and save for whichever page opens it; `unitType` preselects Unit.
export const useOrderUpdateShipment = ({ module, orderId, unitType, open, onClose }) => {
  // currentData, not data: opened from another row, the last order's values must not show.
  const { currentData: orderForShipment, isFetching } = useGetOrderForShipmentQuery(
    { id: orderId },
    { skip: !open || orderId == null },
  );
  const { options, isLoading: isLoadingOptions } = useOrderShipmentFormOptions();
  const [createShipmentForOrder, { isLoading: isSubmitting }] = useCreateShipmentForOrderMutation();

  const initialValues = useMemo(() => {
    if (!orderForShipment) return null;
    const material = options.materials.find(
      (entry) => entry.id === orderForShipment.values.materialId,
    );
    const unit = inheritedShipmentUnit(material, unitType);
    return unit
      ? { ...orderForShipment.values, shipmentQuantityUnit: unit }
      : orderForShipment.values;
  }, [orderForShipment, options.materials, unitType]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        await createShipmentForOrder({ module, orderId, values }).unwrap();
        onClose();
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [createShipmentForOrder, module, orderId, onClose],
  );

  return {
    initialValues,
    // Shown locked in the terminal box when the order holds only a free-text terminal.
    legacyTerminal: orderForShipment?.legacyTerminal ?? '',
    options,
    ready: Boolean(initialValues) && !isLoadingOptions,
    isLoading: isFetching || isLoadingOptions,
    isSubmitting,
    onSubmit,
  };
};
